import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MdDialogRef } from '@angular/material';

import { AlertsService, AlertRow } from 'app/alerts/alerts.service';
import { SnackBarService } from 'app/services/snackbar.service';

/**
 * Detail view for a single screening hit. Users resolve hits on the entity
 * Monitoring tab (direct match resolution — no Phase 1 / Phase 2 case workflow).
 */
@Component({
  selector: 'alert-detail-drawer',
  templateUrl: './alert-detail-drawer.component.html',
  styleUrls: ['./alerts-inbox.component.css']
})
export class AlertDetailDrawerComponent implements OnInit {
  alertSummary: AlertRow;
  isAdminUser = false;
  detail: AlertRow = null;
  loading = false;
  deletingHits = false;
  errorMessage: string = null;

  constructor(
    public dialogRef: MdDialogRef<any>,
    private alertsService: AlertsService,
    private snackBarService: SnackBarService,
    private router: Router
  ) { }

  ngOnInit() {
    this.loadDetail();
  }

  get product(): string {
    return this.alertSummary && this.alertSummary.product;
  }

  get headerLabel(): string {
    if (this.product === 'pep') { return 'PEP'; }
    if (this.product === 'sanctions') { return 'Sanctions'; }
    if (this.product === 'leak_papers') { return 'Leak papers'; }
    return 'Negative news';
  }

  get headerPillClass(): string {
    if (this.product === 'negative_news') { return 'type-pill type-badpress'; }
    if (this.product === 'leak_papers') { return 'type-pill type-leak'; }
    return 'type-pill type-' + (this.product || '');
  }

  get isOpen(): boolean {
    const row = this.detail != null ? this.detail : this.alertSummary;
    return row == null || row.processed !== true;
  }

  get statusLabel(): string {
    const row = this.detail != null ? this.detail : this.alertSummary;
    if (!row || row.processed !== true) { return 'Open'; }
    const r = row.matchResolution;
    if (r === 'True') { return 'Confirmed hit'; }
    if (r === 'False') { return 'False positive'; }
    return 'Resolved';
  }

  get rawPayload(): any {
    if (!this.detail || !this.detail.payloadJson) { return null; }
    try { return JSON.parse(this.detail.payloadJson); } catch (_e) { return null; }
  }

  get rawPayloadEntries(): Array<{ key: string; value: string }> {
    const obj = this.rawPayload;
    if (!obj || typeof obj !== 'object') { return []; }
    const entries: Array<{ key: string; value: string }> = [];
    const add = (o: any, prefix: string) => {
      if (!o || typeof o !== 'object') return;
      for (const k of Object.keys(o).sort()) {
        const v = o[k];
        const key = prefix ? prefix + '.' + k : k;
        if (v == null) {
          entries.push({ key, value: '-' });
        } else if (Array.isArray(v)) {
          entries.push({ key, value: v.map(x => typeof x === 'object' ? JSON.stringify(x) : String(x)).join(', ') });
        } else if (typeof v === 'object') {
          add(v, key);
        } else {
          entries.push({ key, value: String(v) });
        }
      }
    };
    add(obj, '');
    return entries;
  }

  loadDetail() {
    if (!this.alertSummary || !this.alertSummary.alertId) { return; }
    this.loading = true;
    this.alertsService.getAlertDetail(this.alertSummary.alertId).subscribe(
      response => {
        this.loading = false;
        const arr = response && response.arrResult;
        this.detail = (arr && arr.alert) || this.alertSummary;
      },
      err => {
        this.loading = false;
        this.errorMessage = (err && err.message) || 'Failed to load alert detail.';
      }
    );
  }

  /** Open the entity Monitoring tab where the user can resolve this hit directly. */
  resolveOnMonitoring() {
    const row = this.detail != null ? this.detail : this.alertSummary;
    if (!row || !row.entityId) {
      this.snackBarService.showSimpleMsj('Missing entity for this hit.');
      return;
    }
    this.dialogRef.close(false);
    this.router.navigate(['/entity', row.entityId, 'monitoring']);
  }

  deleteAllHitsForEntity() {
    const row = this.detail != null ? this.detail : this.alertSummary;
    if (!this.isAdminUser || !row || !row.entityId || this.deletingHits) {
      return;
    }
    const name = row.entityName || ('Entity ' + row.entityId);
    if (!confirm('Delete all monitoring hits (sanctions, PEP, bad press) for ' + name
        + '? This removes open and resolved hits and cannot be undone.')) {
      return;
    }
    this.deletingHits = true;
    this.alertsService.deleteAllHits(row.entityId).subscribe(
      response => {
        this.deletingHits = false;
        const ok = response && response.flagResult;
        this.snackBarService.showSimpleMsj(
          ok ? 'All hits deleted.' : (response && response.message) || 'Delete failed.');
        if (ok) {
          this.dialogRef.close(true);
        }
      },
      err => {
        this.deletingHits = false;
        this.snackBarService.showSimpleMsj((err && err.message) || 'Failed to delete hits.');
      }
    );
  }

  close() {
    this.dialogRef.close(false);
  }
}



// WEBPACK FOOTER //
// ./src/app/alerts/alert-detail-drawer.component.ts